import React, { useMemo } from 'react';
import { GenericDropdownOption } from '../../../../../../../App.types';
import { Form, Field, FieldProps, FormikHelpers, FormikProps, Formik, ErrorMessage } from 'formik';
import FormField from '../../../../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../../../../ui/DataEntry/Label/Label';
import styles from './PlanForm.module.css';
import Select from '../../../../../../../ui/DataEntry/Select/Select';
import useNonNullContext from '../../../../../../../Hooks/useNonNullContext';
import { MediaPlanApiModel } from '@round/api';
import DatePicker from '../../../../../../../ui/DataEntry/DatePicker/DatePicker';
import TextArea from '../../../../../../../SharedComponents/Forms/TextArea/TextArea';
import { StylesConfig } from 'react-select';
import { OptionsContext } from '../../../../../../../contexts/OptionsContext/OptionsContext';
import Button from '../../../../../../../ui/Buttons/Button/Button';
import * as Yup from 'yup';

export type PlanFormValues = Record<'currency', GenericDropdownOption<number> | undefined> &
    Omit<MediaPlanApiModel, 'release_id' | 'currency_id'>;

type PlanFormProps = {
    handleSubmit: (values: PlanFormValues, helpers: FormikHelpers<PlanFormValues>) => void;
    initialValues: PlanFormValues;
    renderActions?: (props: FormikProps<PlanFormValues>) => React.ReactNode;
};

const selectStyles: StylesConfig = {
    control: (provided, props) => ({
        ...provided,
        cursor: props.isDisabled ? 'not-allowed' : 'pointer',
        fontWeight: 400,
        fontSize: '0.8125rem',
        backgroundColor: props.isDisabled ? '#C2CFE050' : provided.backgroundColor,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0.625rem 1rem',
    }),
    singleValue: (provided, props) => ({
        ...provided,
        color: props.isDisabled ? '#323C4780' : '#323C47',
    }),
    input: (provided) => ({
        ...provided,
        height: '1rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    }),
};

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
};

const defaultRenderActions = ({ isSubmitting, submitForm }: FormikProps<PlanFormValues>) => (
    <div className={styles.defaultRenderActions}>
        <Button
            className={styles.defaultSubmitButton}
            type="filled"
            color="black"
            loading={isSubmitting}
            onClick={submitForm}
        >
            Create
        </Button>
    </div>
);

const PlanForm = ({ handleSubmit, initialValues, renderActions = defaultRenderActions }: PlanFormProps) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const currencyFieldOptions = useMemo(
        () =>
            currencies.map((currency) => ({
                value: currency.id,
                label: currency.name,
            })),
        [currencies]
    );

    const validationSchema = Yup.object({
        budget: Yup.number().required('this field cannot be empty'),
    });

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {(props) => (
                <>
                    <Form className={styles.form}>
                        <div className={styles.row}>
                            <FormField>
                                <Label htmlFor="start_date">Start Date</Label>
                                <Field name="start_date" id="start_date">
                                    {(props: FieldProps<PlanFormValues['start_date'], PlanFormValues>) => (
                                        <DatePicker
                                            dateTimeFormatOptions={dateTimeFormatOptions}
                                            className={styles.dateInput}
                                            value={props.field.value}
                                            onChange={(value) => props.form.setFieldValue('start_date', value)}
                                        />
                                    )}
                                </Field>
                            </FormField>

                            <FormField>
                                <Label htmlFor="end_date">End Date</Label>
                                <Field name="end_date" id="end_date">
                                    {(props: FieldProps<PlanFormValues['end_date'], PlanFormValues>) => (
                                        <DatePicker
                                            dateTimeFormatOptions={dateTimeFormatOptions}
                                            className={styles.dateInput}
                                            value={props.field.value}
                                            onChange={(value) => props.form.setFieldValue('end_date', value)}
                                        />
                                    )}
                                </Field>
                            </FormField>
                        </div>

                        <div className={styles.row}>
                            <FormField>
                                <Label htmlFor="currency">Currency</Label>
                                <Field name="currency">
                                    {(props: FieldProps<PlanFormValues['currency'], PlanFormValues>) => (
                                        <Select
                                            styles={selectStyles}
                                            value={props.field.value}
                                            onChange={(value) => props.form.setFieldValue('currency', value)}
                                            options={currencyFieldOptions}
                                        />
                                    )}
                                </Field>
                            </FormField>

                            <FormField>
                                <Label htmlFor="budget">Budget</Label>
                                <Field name="budget" id="budget" placeholder="Enter budget" type="number" />
                                <ErrorMessage name="budget">
                                    {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                </ErrorMessage>
                            </FormField>
                        </div>

                        <FormField>
                            <Label htmlFor="objective_description">Objective Description</Label>
                            <TextArea
                                name="objective_description"
                                placeholder="Write description"
                                className={styles.objectiveDescription}
                            />
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};

export default PlanForm;
